import React, { useMemo } from "react";
import { Component, ComponentType, Viewport } from "@/types";
import {
  BarChart,
  LineChart,
  PieChart,
  AreaChart,
  ScatterChart,
  HeatmapChart,
  TreemapChart,
  GaugeChart,
  VerticalGaugeChart,
  FermentationTankCard,
  DataCard,
  MapComponent,
} from "@/components/charts";

export interface ChartRendererProps {
  component: Component;
  viewport?: Viewport;
}

export const ChartRenderer: React.FC<ChartRendererProps> = React.memo(
  ({ component, viewport = Viewport.DESKTOP }) => {
    const { type, properties, responsivePosition } = component;

    // Merge responsive breakpoint properties into component properties
    const mergedComponent = useMemo(() => {
      if (!viewport || !responsivePosition?.breakpoints) {
        return component;
      }

      const breakpoint =
        viewport === Viewport.MOBILE
          ? "mobile"
          : viewport === Viewport.TABLET
          ? "tablet"
          : "desktop";

      const breakpointData = responsivePosition.breakpoints[breakpoint];
      const breakpointProperties = breakpointData?.properties;

      if (breakpointProperties && typeof breakpointProperties === "object") {
        return {
          ...component,
          properties: {
            ...properties,
            ...breakpointProperties,
          },
        };
      }

      return component;
    }, [component, viewport, responsivePosition, properties]);

    switch (type) {
      case ComponentType.BAR_CHART:
        return <BarChart component={mergedComponent} />;
      case ComponentType.LINE_CHART:
        return <LineChart component={mergedComponent} />;
      case ComponentType.PIE_CHART:
        return <PieChart component={mergedComponent} />;
      case ComponentType.AREA_CHART:
        return <AreaChart component={mergedComponent} />;
      case ComponentType.SCATTER_CHART:
        return <ScatterChart component={mergedComponent} />;
      case ComponentType.HEATMAP_CHART:
        return <HeatmapChart component={mergedComponent} />;
      case ComponentType.TREEMAP_CHART:
        return <TreemapChart component={mergedComponent} />;
      case ComponentType.GAUGE_CHART:
        return <GaugeChart component={mergedComponent} />;
      case ComponentType.VERTICAL_GAUGE_CHART:
        return <VerticalGaugeChart component={mergedComponent} />;
      case ComponentType.DATA_CARD:
        return <DataCard component={mergedComponent} />;
      case ComponentType.FERMENTATION_TANK_CARD:
        return (
          <FermentationTankCard component={mergedComponent} viewport={viewport} />
        );
      case ComponentType.MAP_CHART:
        return <MapComponent component={mergedComponent} />;
      case ComponentType.CHART: {
        const chartType = mergedComponent.properties.chartType;
        switch (chartType) {
          case "bar":
            return <BarChart component={mergedComponent} />;
          case "line":
            return <LineChart component={mergedComponent} />;
          case "pie":
            return <PieChart component={mergedComponent} />;
          case "area":
            return <AreaChart component={mergedComponent} />;
          case "scatter":
            return <ScatterChart component={mergedComponent} />;
          case "heatmap":
            return <HeatmapChart component={mergedComponent} />;
          case "treemap":
            return <TreemapChart component={mergedComponent} />;
          case "gauge":
            return <GaugeChart component={mergedComponent} />;
          default:
            return <BarChart component={mergedComponent} />;
        }
      }
      default:
        return (
          <div className="w-full h-full flex items-center justify-center bg-gray-50 border border-gray-200 rounded">
            <span className="text-gray-500">Chart: {type}</span>
          </div>
        );
    }
  },
);

ChartRenderer.displayName = "ChartRenderer";
